import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { ButtonModule, ControlValueChangesDirective } from '@pin-up-global/common';
import { BotsLogoComponent } from '@shared/bots-logo';
import { AvatarComponent } from '@shared/avatar';
import { BotSelectorComponent, BotsService } from '@core/bots';
import { ProfileCardComponent, ProfileService } from '@core/profile';
import { MenuItemComponent } from '@shared/menu-item';
import { NgxPermissionsModule } from 'ngx-permissions';

@Component({
  selector: 'ca-sidebar, [caSidebar]',
  standalone: true,
  imports: [
    BotsLogoComponent,
    ButtonModule,
    AvatarComponent,
    ProfileCardComponent,
    BotSelectorComponent,
    ReactiveFormsModule,
    ProfileCardComponent,
    MenuItemComponent,
    TranslateModule,
    AsyncPipe,
    ControlValueChangesDirective,
    MatIcon,
    NgxPermissionsModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'ca-sidebar',
    '[class.ca-sidebar_collapsed]': 'isCollapsed()',
  },
  exportAs: 'ca-sidebar',
})
export class SidebarComponent implements OnInit {
  #botsService = inject(BotsService);
  #profileService = inject(ProfileService);
  #router = inject(Router);
  #destroyRef = inject(DestroyRef);

  botControl = new FormControl<number | null>(null);
  currentLicense = this.#getLicense();

  isCollapsed = signal(false);
  isBotSelected = toSignal(this.botControl.valueChanges.pipe(
      startWith(this.botControl.value),
      map(bot => !!bot),
    ),
  );

  bots$ = this.#botsService.bots$;
  isBotsLoading$ = this.#botsService.isLoading$;
  profile$ = this.#profileService.profile$;

  ngOnInit(): void {
    this.#botsService.selectedBotId$
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(botId => {
          this.botControl.setValue(botId);
        });
  }

  botChanged(botId: number): void {
    this.#botsService.updateSelectedBotId(botId);
    this.#router.navigate(['/', this.currentLicense, 'bot', botId]);
  }

  toggle(): void {
    this.isCollapsed.update(isCollapsed => !isCollapsed);
  }

  #getLicense(): string {
    return this.#router.url.split('/').at(1)
  }
}
